const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3333'

async function getAuthToken(): Promise<string | null> {
  return (window as any).Clerk?.session?.getToken() ?? null
}

async function getHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken()
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export const api = {
  async get(url: string) {
    const response = await fetch(`${API_URL}${url}`, {
      headers: await getHeaders(),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error(`Failed to GET ${url}`)
    }
    return response.json()
  },

  async post(url: string, data: any) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      const error = await response.json()
      throw new Error(error.message || `Failed to POST ${url}`)
    }
    return response.json()
  },

  async put(url: string, data: any) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: await getHeaders(),
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      const error = await response.json()
      throw new Error(error.message || `Failed to PUT ${url}`)
    }
    return response.json()
  },

  async delete(url: string) {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      if (response.status === 409) throw new Error('Impossible de supprimer cette ressource (conflit)')
      const error = await response.json()
      throw new Error(error.message || `Failed to DELETE ${url}`)
    }
    return response.json()
  },

  async getClasses() {
    const response = await fetch(`${API_URL}/api/classes`, {
      headers: await getHeaders(),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to fetch classes')
    }
    const data = await response.json()
    if (data.classes && Array.isArray(data.classes)) return data.classes
    if (Array.isArray(data)) return data
    return []
  },

  async createClass(classData: { name: string; level: string; year: string; description?: string }) {
    const response = await fetch(`${API_URL}/api/classes`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(classData),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      const error = await response.json()
      throw new Error(error.message || 'Failed to create class')
    }
    const data = await response.json()
    return data.class || data
  },

  async updateClass(id: string, classData: { name: string; level: string; year: string; description?: string }) {
    const response = await fetch(`${API_URL}/api/classes/${id}`, {
      method: 'PUT',
      headers: await getHeaders(),
      body: JSON.stringify(classData),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      const error = await response.json()
      throw new Error(error.message || 'Failed to update class')
    }
    const data = await response.json()
    return data.class || data
  },

  async deleteClass(id: string) {
    const response = await fetch(`${API_URL}/api/classes/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      if (response.status === 409) throw new Error('Impossible de supprimer une classe contenant des élèves.')
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete class')
    }
    return response.json()
  },

  async importFile(classId: string, fileData: string, fileName: string, replaceExisting: boolean = false) {
    const response = await fetch(`${API_URL}/api/classes/${classId}/import-file`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ fileData, fileName, replaceExisting }),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      if (response.status === 404) throw new Error('Route d\'import non disponible.')
      const error = await response.json()
      throw new Error(error.message || 'Failed to import file')
    }
    return response.json()
  },

  async getStudents(classId?: string) {
    const url = classId
      ? `${API_URL}/api/students?classId=${classId}`
      : `${API_URL}/api/students`
    const response = await fetch(url, { headers: await getHeaders() })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to fetch students')
    }
    return response.json()
  },

  async saveStudents(students: any[]) {
    const response = await fetch(`${API_URL}/api/students`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ students }),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to save students')
    }
    return response.json()
  },

  async deleteStudent(id: string) {
    const response = await fetch(`${API_URL}/api/students/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to delete student')
    }
    return response.json()
  },

  async getEvaluations() {
    const response = await fetch(`${API_URL}/api/evaluations`, { headers: await getHeaders() })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to fetch evaluations')
    }
    return response.json()
  },

  async saveEvaluations(evaluations: any) {
    const response = await fetch(`${API_URL}/api/evaluations`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ evaluations }),
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to save evaluations')
    }
    return response.json()
  },

  async getComments() {
    const response = await fetch(`${API_URL}/api/comments`, { headers: await getHeaders() })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Non authentifié. Veuillez vous reconnecter.')
      throw new Error('Failed to fetch comments')
    }
    return response.json()
  },
}

export default api
